import { useState } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { BadgeCheck, XCircle, Clock, X, ExternalLink } from "lucide-react";
import { getImageUrl } from "../../utils/getImageUrl";

export default function UserVerificationModal({ user, onClose, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const setStatus = async (status) => {
    setLoading(true);
    try {
      await API.put(`/users/${user.id}/verify`, { status });
      toast.success(
        status === "aprobado" ? "Usuario verificado correctamente" :
        status === "rechazado" ? "Documento rechazado" : "Estado actualizado"
      );
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error actualizando verificación");
    } finally {
      setLoading(false);
    }
  };

  const docUrl = getImageUrl(user.documentPhoto);
  const isPdf = user.documentPhoto?.toLowerCase().endsWith(".pdf");
  const current = user.verificationStatus || "pendiente";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10020] p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Verificación de documento</h2>
            <p className="text-sm text-gray-500 mt-0.5">{user.name} · {user.email}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Current status */}
        <div className="px-5 pt-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado actual:</span>
            {current === "aprobado" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                <BadgeCheck size={12} /> Verificado
              </span>
            )}
            {current === "pendiente" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-yellow-50 text-yellow-700 border border-yellow-200">
                <Clock size={12} /> En revisión
              </span>
            )}
            {current === "rechazado" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-50 text-red-600 border border-red-200">
                <XCircle size={12} /> Rechazado
              </span>
            )}
          </div>

          {/* Document preview */}
          <div className="rounded-xl border border-gray-200 overflow-hidden mb-4 bg-gray-50">
            {isPdf ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <span className="text-4xl">📄</span>
                <p className="text-sm text-gray-500">Documento PDF</p>
                <a
                  href={docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-semibold hover:underline"
                >
                  <ExternalLink size={14} /> Abrir PDF
                </a>
              </div>
            ) : (
              <div className="relative group">
                {imageFailed ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2 text-sm text-gray-500">
                    <span>No se pudo cargar la imagen.</span>
                    <a href={docUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">
                      Abrir en una pestaña nueva
                    </a>
                  </div>
                ) : (
                  <img
                    src={docUrl}
                    alt="Documento de identidad"
                    onError={() => setImageFailed(true)}
                    className="w-full max-h-72 object-contain"
                  />
                )}
                <a
                  href={docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-2 right-2 bg-black/60 text-white rounded-lg px-2.5 py-1.5 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition"
                >
                  <ExternalLink size={12} /> Ver completo
                </a>
              </div>
            )}
          </div>

          {/* Extra info */}
          {(user.documentType || user.documentNumber) && (
            <div className="flex gap-3 mb-4">
              {user.documentType && (
                <div className="flex-1 bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <p className="text-xs text-gray-400 mb-0.5">Tipo</p>
                  <p className="font-semibold text-gray-800 text-sm">{user.documentType}</p>
                </div>
              )}
              {user.documentNumber && (
                <div className="flex-1 bg-gray-50 rounded-xl p-3 border border-gray-100">
                  <p className="text-xs text-gray-400 mb-0.5">Número</p>
                  <p className="font-semibold text-gray-800 text-sm">{user.documentNumber}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 border-t bg-gray-50 rounded-b-2xl flex gap-3">
          <button
            onClick={() => setStatus("rechazado")}
            disabled={loading || current === "rechazado"}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-600 font-semibold text-sm hover:bg-red-100 disabled:opacity-50 transition"
          >
            <XCircle size={15} /> Rechazar
          </button>
          <button
            onClick={() => setStatus("pendiente")}
            disabled={loading || current === "pendiente"}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 font-semibold text-sm hover:bg-gray-100 disabled:opacity-50 transition"
          >
            <Clock size={15} />
          </button>
          <button
            onClick={() => setStatus("aprobado")}
            disabled={loading || current === "aprobado"}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-bold text-sm disabled:opacity-50 transition"
          >
            <BadgeCheck size={15} /> {loading ? "Guardando..." : "Aprobar"}
          </button>
        </div>
      </div>
    </div>
  );
}
